import React, { forwardRef } from 'react';
import { observer } from 'mobx-react';
import { DatePicker as MUIDatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { Controller, useFormContext } from 'react-hook-form';
import { Box } from '@mui/material';
import { useEntityPermission } from '../../providers/EntityPermissionProvider/index';
import ReadOnly from './ReadOnly';

export const DATE_FORMAT = {
  SHORT_DATE: 'MMM D',
  MEDIUM_DATE: 'MMM D, YYYY',
  LONG_DATE: 'MMM D, YYYY h:mm A',
  STANDARD_DATE: 'YYYY-MM-DD',
  STANDARD_DATETIME: 'YYYY-MM-DDTHH:mm:ss',
  /** e.g. Tuesday, April 14, 2026 */
  FRIENDLY_WEEKDAY_DATE: 'dddd, MMMM D, YYYY',
};

const DatePicker = forwardRef(
  (
    { overwriteProps = {}, name, onChange, error, helperText, ...props },
    ref,
  ) => {
    if (!name) {
      throw new Error('DatePicker requires a "name" prop');
    }

    const { getValues } = useFormContext({});

    const { isHidden, isReadOnly } = useEntityPermission();

    const currentvalue = getValues(name);

    if (isReadOnly(props?.permission, currentvalue)) {
      return <ReadOnly name={name}>{currentvalue}</ReadOnly>;
    }

    if (isHidden(props?.permission)) {
      return <input type="hidden" name={name} value={currentvalue} />;
    }

    return (
      <Box>
        <Controller
          name={name}
          render={({ field, fieldState: { error: fsError } }) => {
            return (
              <MUIDatePicker
                ref={ref}
                label={props.label}
                {...field}
                {...props}
                value={field.value ? dayjs(field.value) : null}
                onChange={e => {
                  const target = {
                    name: `${field?.name}`,
                    value: e
                      ? dayjs(e).format(DATE_FORMAT.STANDARD_DATE) || null
                      : null,
                  };
                  field.onChange({ target });
                }}
                error={Boolean(fsError?.message || error)}
                sx={{
                  '& .MuiPickersSectionList-root.MuiPickersInputBase-sectionsContainer ':
                    {
                      padding: '6px 0px 6px 6px!important',
                    },
                  minWidth: '100%',
                  ...(props.sx || {}),
                }}
                {...(props?.disablePast
                  ? {
                      minDate: dayjs().startOf('year'),
                    }
                  : { minDate: dayjs('1990-01-01') })}
                slotProps={{
                  textField: {
                    error: !!fsError,
                    helperText: fsError?.message,
                    size: 'small',
                  },
                  field: {
                    clearable: true,
                    onClear: () => {
                      const target = {
                        name: props?.name,
                        value: null,
                      };
                      field.onChange({ target });
                    },
                  },
                }}
                {...overwriteProps}
              />
            );
          }}
        />
      </Box>
    );
  },
);

export default observer(DatePicker);
